'use client';
import * as RadixDialog from '@radix-ui/react-dialog';
import { ColumnDef } from '@tanstack/react-table';
import dynamic from 'next/dynamic';
import { useRef, useState, useEffect } from 'react';
import { useMediaQuery } from 'react-responsive';

import { useSession } from '@lichtblick/auth';
import { useTranslation } from '@lichtblick/i18next';
import { Article, Close, Delete, Download, VideoCameraFront } from '@lichtblick/icons';
import { TextVariants } from '@lichtblick/theme';
import { GetWorkOrderFilesMediaPayloadModel } from '@lichtblick/types';
import { deleteFile, formatDate, formatName, BREAKPOINTS } from '@lichtblick/utils';

import {
  BtnWrapper,
  CarouselContainerOverlay,
  CarouselDocContainer,
  CarouselVideoContainer,
  DeleteContainer,
  DeleteDialog,
  IconButtonClose,
  PreviewWrapper,
} from './DocPreview.styles';
import { DownloadAction } from './DownloadAction';
import { mimeTypeMapper, imageMimeType, docMimeType, videoMimeType } from './utils';

import { Button } from '../Button';
import { Carousel } from '../Carousel';
import { ChatMessageImageRow } from '../ChatMessageImageRow';
import { IconButton } from '../IconButton';
import { Snackbar } from '../Snackbar';
import { GenericTable } from '../Table';
import { Text } from '../Text';

const DocPreview = dynamic(() => import('./DocPreview').then((mod) => mod.DocPreview), {
  ssr: false,
});

export const FileContent = ({
  allowMultiSelect = true,
  data,
  downloadLabel,
  folderId,
  isDownloading,
  isMonday,
  onDownload,
  onSelectionChange,
  refetch,
  showDownloadAction,
  workOrderId,
}: {
  allowMultiSelect?: boolean;
  data: GetWorkOrderFilesMediaPayloadModel[];
  downloadLabel?: string;
  folderId?: number;
  isDownloading?: boolean;
  isMonday?: boolean;
  onDownload?: () => void;
  onSelectionChange?: (ids: number[]) => void;
  refetch: () => void;
  showDownloadAction?: boolean;
  workOrderId: number;
}) => {
  const { t } = useTranslation();
  const { data: session } = useSession();
  const isMobile = useMediaQuery({ maxWidth: BREAKPOINTS.SMALL });
  const [rowToDelete, setRowToDelete] = useState<string | null>(null);
  const [isShowingCarousel, setIsShowingCarousel] = useState<boolean>(false);
  const [selectedFiles, setSelectedFiles] = useState<Set<number>>(new Set());
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>('');
  const [showSnackbar, setShowSnackbar] = useState<boolean>(false);
  const handleRef = useRef<{ goToSlide: (idx: number) => void }>(null);
  const [activeSlideIndex, setActiveSlideIndex] = useState<number>(0);

  // Clear selection when data changes
  useEffect(() => {
    setSelectedFiles(new Set());
    onSelectionChange?.([]);
  }, [data, onSelectionChange]);

  const renderByMimeType = (item: GetWorkOrderFilesMediaPayloadModel, isCarousel?: boolean, shouldFetch?: boolean) => {
    if (!item.mimeType) {
      return <Article />;
    }

    return (
      <PreviewWrapper>
        {imageMimeType.includes(item.mimeType) ? (
          isCarousel ? (
            <CarouselDocContainer>
              <RadixDialog.DialogTitle>{item.fileName}</RadixDialog.DialogTitle>
              <div
                style={{
                  pointerEvents: 'auto',
                  height: '100%',
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <ChatMessageImageRow
                  expectedMaxWidth={900}
                  hasModal={false}
                  image={{
                    contentType: item.mimeType,
                    url: item.uri ?? '',
                  }}
                  isFileBrowser={true}
                  isInListView={false}
                />
              </div>
            </CarouselDocContainer>
          ) : (
            <ChatMessageImageRow
              expectedMaxWidth={isMobile ? 40 : 80}
              hasModal={false}
              image={{
                contentType: item.mimeType,
                url: item.uri ?? '',
              }}
              isFileBrowser={true}
              isInListView={false}
            />
          )
        ) : videoMimeType.includes(item.mimeType) ? (
          isCarousel ? (
            <CarouselVideoContainer>
              {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
              <video
                controls
                playsInline
                src={item.uri ?? ''}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                }}
              >
                Your browser does not support the video tag.
              </video>
            </CarouselVideoContainer>
          ) : (
            <VideoCameraFront />
          )
        ) : docMimeType.includes(item.mimeType) ? (
          isCarousel ? (
            <CarouselDocContainer>
              <RadixDialog.DialogTitle>{item.fileName}</RadixDialog.DialogTitle>
              <div style={{ pointerEvents: 'auto', height: '100%', width: '100%' }}>
                <DocPreview
                  doc={{ contentType: item.mimeType, uri: item.uri ?? '', size: item.fileSize }}
                  hasModal={false}
                  isMonday={isMonday}
                  shouldFetch={shouldFetch}
                />
              </div>
            </CarouselDocContainer>
          ) : (
            <Article />
          )
        ) : (
          <Article />
        )}
      </PreviewWrapper>
    );
  };

  const handleOpenCarousel = (id: number) => {
    const idx = data.findIndex((d) => d.id === id);

    setActiveSlideIndex(idx);
    setIsShowingCarousel(true);
    setTimeout(() => {
      handleRef.current?.goToSlide(idx);
    }, 0);
  };

  const handleSlideChange = (newIndex: number) => {
    setActiveSlideIndex(newIndex);
  };

  const handleSelectionChange = (newSelection: Set<string | number>) => {
    const nextSelection = [...newSelection].map((id) => Number(id));

    setSelectedFiles(new Set(nextSelection));
    onSelectionChange?.(nextSelection);
  };

  const handleBulkDelete = async () => {
    if (selectedFiles.size === 0) {
      return;
    }

    setIsDeleting(true);
    let deletedCount = 0;
    let failedCount = 0;

    for (const fileId of selectedFiles) {
      const file = data.find((f) => f.id === fileId);

      if (file) {
        try {
          await deleteFile(session, file.uri, folderId, file.id, workOrderId);
          deletedCount++;
        } catch (error) {
          failedCount++;
          // eslint-disable-next-line no-console
          console.error('Failed to delete file:', file.fileName, error);
        }
      }
    }

    setIsDeleting(false);
    setSelectedFiles(new Set());
    setShowBulkDeleteDialog(false);
    refetch();

    if (failedCount > 0) {
      setSnackbarMessage(t('pm:deleteBulkPartialSuccess', { deletedCount, failedCount }));
    } else {
      setSnackbarMessage(t('pm:deleteBulkSuccess', { deletedCount }));
    }

    setShowSnackbar(true);
  };

  const name = ({ row }: { row: { original: GetWorkOrderFilesMediaPayloadModel } }) => (
    <Text $variant={TextVariants.body.m}>{row.original.fileName}</Text>
  );
  const format = ({ row }: { row: { original: GetWorkOrderFilesMediaPayloadModel } }) => (
    <Text $variant={TextVariants.body.m}>{mimeTypeMapper[row.original.mimeType as string]}</Text>
  );
  const preview = ({ row }: { row: { original: GetWorkOrderFilesMediaPayloadModel } }) => {
    return (
      <div onClick={() => handleOpenCarousel(row.original.id)} style={{ cursor: 'pointer', width: '100%' }}>
        {renderByMimeType(row.original)}
      </div>
    );
  };

  const owner = ({ row }: { row: { original: GetWorkOrderFilesMediaPayloadModel } }) => (
    <Text $variant={TextVariants.body.m}>{row.original.createdBy ? formatName(row.original.createdBy) : ''}</Text>
  );
  const createAt = ({ row }: { row: { original: GetWorkOrderFilesMediaPayloadModel } }) => (
    <Text $variant={TextVariants.body.m}>{formatDate(row.original.createdAtUtc)}</Text>
  );
  const download = ({ row }: { row: { original: GetWorkOrderFilesMediaPayloadModel } }) => {
    return (
      <BtnWrapper>
        {isMobile ? (
          <IconButton
            icon={<Download />}
            onClick={() => {
              const link = document.createElement('a');

              link.href = row.original.uri ?? '';
              link.download = row.original.fileName ?? 'download';
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            }}
            tooltip={t('download')}
          />
        ) : (
          <DownloadAction row={row} />
        )}
        <DeleteDialog
          acceptLabel={t('delete')}
          cancelLabel={t('cancel')}
          description={t('pm:deleteFile') ?? ''}
          isOpen={rowToDelete === row.original.uri}
          onAccept={async () => {
            await deleteFile(session, row.original.uri, folderId, row.original.id, workOrderId);
            refetch();
            setRowToDelete(null);
          }}
          onCancel={() => setRowToDelete(null)}
          onClose={() => setRowToDelete(null)}
          trigger={
            <IconButton
              icon={<Delete />}
              onClick={() => {
                if (row.original.uri) {
                  setRowToDelete(row.original.uri);
                }
              }}
              variant="rejection"
            />
          }
        />
      </BtnWrapper>
    );
  };

  const columns: ColumnDef<GetWorkOrderFilesMediaPayloadModel>[] = [
    {
      accessorKey: 'fileName',
      header: 'Name',
      // size: 30,
      cell: name,
      enableSorting: false,
    },
    ...(isMobile
      ? []
      : [
          {
            accessorKey: 'mimeType' as keyof GetWorkOrderFilesMediaPayloadModel,
            header: 'Format',
            cell: format,
            enableSorting: false,
          },
        ]),
    {
      header: 'Preview',
      size: 30,
      cell: preview,
      meta: { shouldEscapeLink: true },
    },
    ...(isMobile
      ? []
      : [
          {
            accessorKey: 'createdBy' as keyof GetWorkOrderFilesMediaPayloadModel,
            header: 'Owner',
            cell: owner,
            enableSorting: false,
          },
          {
            accessorKey: 'createdAtUtc' as keyof GetWorkOrderFilesMediaPayloadModel,
            header: 'Added',
            cell: createAt,
            enableSorting: false,
          },
        ]),
    {
      id: 'actions',
      header: '',
      cell: download,
      enableSorting: false,
      meta: { shouldEscapeLink: true },
    },
  ];

  if (!data || data.length == 0) {
    return <Text $variant={TextVariants.body.m}>{t('pm:noFileData')}</Text>;
  }

  return (
    <>
      {allowMultiSelect && selectedFiles.size > 0 && (
        <DeleteContainer>
          <Text $variant={TextVariants.body.m}>
            {selectedFiles.size} {selectedFiles.size === 1 ? 'file' : 'files'} selected
          </Text>
          {showDownloadAction && downloadLabel ? (
            <Button isLoading={isDownloading} label={downloadLabel} onClick={() => onDownload?.()} />
          ) : null}
          <Button
            isDisabled={isDeleting}
            label={isDeleting ? 'Deleting...' : 'Delete Selected'}
            onClick={() => setShowBulkDeleteDialog(true)}
            variant="rejection"
          />
        </DeleteContainer>
      )}

      <GenericTable
        columns={columns}
        data={data}
        enableRowSelection={allowMultiSelect}
        isFullHeight={false}
        onRowSelectionChange={handleSelectionChange}
        rowIdAccessor="id"
        selectedRows={new Set([...selectedFiles].map((id) => id))}
      />

      <DeleteDialog
        acceptLabel={t('delete')}
        cancelLabel={t('cancel')}
        description={t('pm:deleteBulkFiles', { count: selectedFiles.size })}
        isOpen={showBulkDeleteDialog}
        onAccept={handleBulkDelete}
        onCancel={() => setShowBulkDeleteDialog(false)}
        onClose={() => setShowBulkDeleteDialog(false)}
      />

      <RadixDialog.Root open={isShowingCarousel}>
        <RadixDialog.Portal>
          <CarouselContainerOverlay
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setIsShowingCarousel(false);
              }
            }}
          >
            <Carousel
              onSlideChange={handleSlideChange}
              ref={handleRef}
              sliders={data.map((item, idx) => ({
                id: item.id,
                // shouldFetch ist nur true, wenn idx === activeSlideIndex
                content: renderByMimeType(item, true, idx === activeSlideIndex),
              }))}
            />
            <RadixDialog.Close asChild>
              <IconButtonClose icon={<Close />} onClick={() => setIsShowingCarousel(false)} />
            </RadixDialog.Close>
          </CarouselContainerOverlay>
        </RadixDialog.Portal>
      </RadixDialog.Root>

      <Snackbar
        color="black"
        isOpen={showSnackbar}
        message={snackbarMessage}
        setOpen={setShowSnackbar}
        title={t('pm:deleteBulkSuccessTitle')}
      />
    </>
  );
};
